import { useClientTranslation } from '@/lib/core/i18n';
import { formatAmount } from '@/lib/core/utils';
import type { CartProductItemDto } from '@/services/platform/api/model';

interface DeliverTo {
  label: string;
  detail: string;
}

interface OrderSummaryAccordionContentProps {
  items: CartProductItemDto[];
  subtotal: number;
  shipping: number;
  discount: number;
  total: number;
  currencyCode: string;
  locale: string;
  giftMessage?: string;
  deliverTo?: DeliverTo;
}

/**
 * Shared "VIEW ORDER SUMMARY" / "ORDER SUMMARY" accordion content (Figma:
 * Checkout | Payment methods - view booking summary, node 3991:79828; and
 * Checkout | Order Confirmed, node 3991:80477) — same layout, fed either
 * from the live cart (pre-purchase) or the closed-order summary (post-purchase).
 */
export function OrderSummaryAccordionContent({
  items,
  subtotal,
  shipping,
  discount,
  total,
  currencyCode,
  locale,
  giftMessage,
  deliverTo,
}: OrderSummaryAccordionContentProps) {
  const t = useClientTranslation();

  return (
    <>
      <div className="border-jncquoi-neutral-200 flex flex-col gap-2 border-b-[0.5px] pb-4">
        <SummaryRow label={t('App.Shop.Subtotal')} value={formatAmount(subtotal, currencyCode, locale)} />
        <SummaryRow
          label={t('App.Shop.Shipping')}
          hint={t('App.Shop.ShippingCalculatedNote')}
          value={formatAmount(shipping, currencyCode, locale)}
        />
        <SummaryRow
          label={t('App.Shop.AppliedDiscount')}
          value={formatAmount(discount, currencyCode, locale)}
        />
        <div className="flex items-center gap-4">
          <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 min-w-0 flex-1 text-[14px] leading-normal font-semibold tracking-[0.5px]">
            {t('App.Shop.Total')}
          </span>
          <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[16px] leading-normal font-semibold tracking-[0.25px] whitespace-nowrap">
            {formatAmount(total, currencyCode, locale)}
          </span>
        </div>
      </div>

      {items.map(item => (
        <div
          key={item.variantId}
          className="border-jncquoi-neutral-200 flex items-start gap-4 border-b-[0.5px] py-4"
        >
          <div className="bg-jncquoi-neutral-100 relative h-23 w-16 shrink-0 overflow-hidden">
            {item.imageUrl && (
              <img
                src={item.imageUrl}
                alt={item.name ?? ''}
                className="absolute inset-0 h-full w-full object-cover"
              />
            )}
          </div>
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            {item.vendor && (
              <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[10px] leading-none font-semibold tracking-[0.5px] uppercase">
                {item.vendor}
              </span>
            )}
            <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[12px] leading-relaxed tracking-[0.5px]">
              {item.name}
            </span>
            <span className="font-pp-neue-montreal text-jncquoi-neutral-700 text-[12px] leading-relaxed tracking-[0.25px]">
              {[
                ...(item.options?.map(option => option.value).filter(Boolean) ?? []),
                `${t('App.Shop.QuantityAbbr')}: ${item.quantity ?? 1}`,
              ].join(' • ')}
            </span>
          </div>
          <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[14px] leading-none font-semibold whitespace-nowrap">
            {formatAmount(item.totalPrice ?? item.unitPrice ?? 0, currencyCode, locale)}
          </span>
        </div>
      ))}

      {giftMessage && (
        <div className="border-jncquoi-neutral-200 flex items-start gap-6 border-b-[0.5px] py-4">
          <span className="font-pp-neue-montreal w-30 shrink-0 text-[12px] leading-normal tracking-[0.5px] text-black uppercase">
            {t('App.Shop.GiftSummaryLabel')}
          </span>
          <div className="flex min-w-0 flex-1 flex-col text-right">
            <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[14px] leading-normal tracking-[0.5px]">
              {t('App.Shop.PersonalMessageIncluded')}
            </span>
            <span className="font-pp-neue-montreal text-jncquoi-neutral-700 text-[12px] leading-normal tracking-[0.5px] italic">
              {giftMessage}
            </span>
          </div>
        </div>
      )}

      {deliverTo && (
        <div className="flex items-start gap-6 py-4">
          <span className="font-pp-neue-montreal w-30 shrink-0 text-[12px] leading-normal tracking-[0.5px] text-black uppercase">
            {t('App.Shop.DeliverToSummaryLabel')}
          </span>
          <div className="flex min-w-0 flex-1 flex-col text-right">
            <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[14px] leading-normal tracking-[0.5px]">
              {deliverTo.label}
            </span>
            <span className="font-pp-neue-montreal text-jncquoi-neutral-700 text-[12px] leading-normal tracking-[0.5px]">
              {deliverTo.detail}
            </span>
          </div>
        </div>
      )}
    </>
  );
}

function SummaryRow({ label, hint, value }: { label: string; hint?: string; value: string }) {
  return (
    <div className="flex items-center gap-4">
      <span className="font-pp-neue-montreal text-jncquoi-neutral-700 min-w-0 flex-1 text-[14px] leading-normal font-normal tracking-[0.5px]">
        {label}
        {hint && <span className="ml-1 text-[12px]">{hint}</span>}
      </span>
      <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[14px] leading-normal font-normal tracking-[0.5px] whitespace-nowrap">
        {value}
      </span>
    </div>
  );
}
